import type { PropertyMediaAssetEntity } from "@/types/property";

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function addUrl(target: string[], seen: Set<string>, value: unknown) {
  const url = asString(value);
  if (!url || seen.has(url)) return;
  seen.add(url);
  target.push(url);
}

/** True when media assets contain at least one displayable gallery/fallback image. */
export function propertyHasDisplayableImages(assets: PropertyMediaAssetEntity[] | null | undefined): boolean {
  if (!assets?.length) return false;

  for (const asset of assets) {
    const gallery = Array.isArray(asset.galleryImages) ? asset.galleryImages : [];

    if (gallery.length === 0) {
      if (asString(asset.internalThumbnailUrl) ?? asString(asset.internalUrl)) {
        return true;
      }
      continue;
    }

    for (const raw of gallery) {
      const image = (raw ?? {}) as Record<string, unknown>;
      const thumbnailUrl =
        asString(image.internalSizesThumbnailSrc) ??
        asString(image.internalSizesTileSrc) ??
        asString(image.internalSizesScreenSrc) ??
        asString(asset.internalThumbnailUrl) ??
        asString(asset.internalUrl);

      if (thumbnailUrl) return true;
    }
  }

  return false;
}

/** First browser-loadable image for the property list thumbnail. */
export function resolveFirstPropertyImageUrl(property: unknown): string | null {
  return resolvePropertyImageUrls(property)[0] ?? null;
}

function isApiSafeImageUrl(url: string | null | undefined): url is string {
  const normalized = url?.trim();
  return Boolean(normalized && !normalized.startsWith("data:"));
}

/** Resolves the best available image URL for a selected property in Reach Out. */
export function resolveSelectedPropertyImageUrl(params: {
  propertyId: string | null | undefined;
  propertyDetail: unknown;
  propertySummary: unknown;
  previewImageUrlsByPropertyId?: Record<string, string[]>;
}): string | null {
  const { propertyId, propertyDetail, propertySummary, previewImageUrlsByPropertyId } =
    params;

  if (!propertyId) return null;

  return (
    resolveFirstPropertyImageUrl(propertyDetail) ??
    resolveFirstPropertyImageUrl(propertySummary) ??
    previewImageUrlsByPropertyId?.[propertyId]?.[0] ??
    null
  );
}

/** Like {@link resolveSelectedPropertyImageUrl} but skips base64 data URLs (invalid for API payloads). */
export function resolveSelectedPropertyImageUrlForApi(params: {
  propertyId: string | null | undefined;
  propertyDetail: unknown;
  propertySummary: unknown;
  previewImageUrlsByPropertyId?: Record<string, string[]>;
}): string | null {
  const { propertyId, propertyDetail, propertySummary, previewImageUrlsByPropertyId } =
    params;

  if (!propertyId) return null;

  const candidates = [
    resolveFirstPropertyImageUrl(propertyDetail),
    resolveFirstPropertyImageUrl(propertySummary),
    previewImageUrlsByPropertyId?.[propertyId]?.[0] ?? null,
  ];

  return candidates.find(isApiSafeImageUrl) ?? null;
}

/** Ordered thumbnail candidates; callers can try the next URL if one fails to render. */
export function resolvePropertyImageUrls(property: unknown): string[] {
  if (!property || typeof property !== "object") return [];

  const raw = property as Record<string, unknown>;
  const urls: string[] = [];
  const seen = new Set<string>();

  addUrl(urls, seen, raw.internalThumbnailUrl);
  addUrl(urls, seen, raw.internalUrl);
  addUrl(urls, seen, raw.thumbnailUrl);
  addUrl(urls, seen, raw.imageUrl);
  addUrl(urls, seen, raw.primaryImageUrl);
  addUrl(urls, seen, raw.mainImageUrl);
  addUrl(urls, seen, raw.url);

  const mediaAssets = Array.isArray(raw.mediaAssets)
    ? (raw.mediaAssets as PropertyMediaAssetEntity[])
    : Array.isArray(raw.media)
      ? (raw.media as PropertyMediaAssetEntity[])
      : [];

  for (const asset of mediaAssets) {
    const gallery = Array.isArray(asset.galleryImages) ? asset.galleryImages : [];
    const images = Array.isArray(asset.images) ? asset.images : [];
    const mediaImages = gallery.length > 0 ? gallery : images;

    if (mediaImages.length === 0) {
      addUrl(urls, seen, asset.internalThumbnailUrl);
      addUrl(urls, seen, asset.internalUrl);
      addUrl(urls, seen, asset.thumbnailUrl);
      addUrl(urls, seen, asset.url);
      continue;
    }

    for (const rawImage of mediaImages) {
      const image = (rawImage ?? {}) as Record<string, unknown>;
      addUrl(urls, seen, image.internalSizesThumbnailSrc);
      addUrl(urls, seen, image.internalSizesTileSrc);
      addUrl(urls, seen, image.internalSizesScreenSrc);
      addUrl(urls, seen, image.internalUrl);
      addUrl(urls, seen, asset.internalThumbnailUrl);
      addUrl(urls, seen, asset.internalUrl);
      addUrl(urls, seen, image.sizesThumbnailSrc);
      addUrl(urls, seen, image.sizesTileSrc);
      addUrl(urls, seen, image.sizesScreenSrc);
      addUrl(urls, seen, image.thumbnailUrl);
      addUrl(urls, seen, image.screenUrl);
      addUrl(urls, seen, image.url);
      addUrl(urls, seen, asset.thumbnailUrl);
      addUrl(urls, seen, asset.url);
    }
  }

  return urls;
}
